import { FORM_ID_DESCRIPTIONS, FormID } from '@finxone-platform/form-action';
import {
  NewWorkflowWidgetConfig,
  ProjectFormsConfig,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';

export function getAllFormIdsFromEnum(): string[] {
  return Object.values(FormID).filter((v) => typeof v === 'string') as string[];
}

// New: return ordered key/value pairs (with descriptions) based on the provided table
export function getFormIdKeyValuePairs(): { key: string; value: string; description?: string }[] {
  // Object.entries on a string enum yields [key, value] pairs
  return Object.entries(FormID)
    .filter(([_, v]) => typeof v === 'string')
    .map(([k, v]) => ({
      key: k,
      value: v as string,
      // v is the enum value (typed as FormID for indexing the typed map)
      description: FORM_ID_DESCRIPTIONS[v as FormID],
    }));
}

export function computeNumberOfInputs(
  children: NewWorkflowWidgetConfig[][] | NewWorkflowWidgetConfig[] | undefined,
): number {
  if (!Array.isArray(children)) return 0;

  const countInArray = (arr: any[]): number => {
    let cnt = 0;
    for (const it of arr) {
      if (Array.isArray(it)) {
        cnt += countInArray(it);
        continue;
      }
      if (!it || typeof it !== 'object') continue;
      if (it.name === WidgetNames.INPUT_WIDGET) {
        cnt += 1;
      }
      // Recurse into container widgets (e.g. section, two-column, sub-container, tab)
      // so inputs nested inside them are also counted.
      if (Array.isArray(it.childComponents)) {
        cnt += countInArray(it.childComponents);
      }
    }
    return cnt;
  };

  return countInArray(children);
}

// Recursively set inputFieldConfig.parentForm on child widget configs.
export function assignParentFormToChildren(children: any[] | undefined, parentFormId: string) {
  if (!Array.isArray(children)) return;
  for (const item of children) {
    if (Array.isArray(item)) {
      // nested container - recurse
      assignParentFormToChildren(item, parentFormId);
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    // Ensure path exists and set parentForm when inputFieldConfig exists (or create it)
    item.widgetConfiguration = item.widgetConfiguration || {};
    item.widgetConfiguration.attributes = item.widgetConfiguration.attributes || {};
    item.widgetConfiguration.attributes.widgetProperties =
      item.widgetConfiguration.attributes.widgetProperties || {};

    const wp = item.widgetConfiguration.attributes.widgetProperties;
    wp.inputFieldConfig = wp.inputFieldConfig || {};
    wp.inputFieldConfig.parentForm = parentFormId;
  }
}

/**
 * Removes fields from the provided project form that match any of the supplied IDs.
 * Public so it can be reused in other places where fields need to be removed by id.
 */
export function removeFieldsFromProjectFormByIds(form: ProjectFormsConfig | undefined, ids: string[]) {
  if (form && ids && ids.length > 0) {
    form.fieldsConfig = form.fieldsConfig.filter((field) => !ids.includes(field.id));
  }
}
